/**
 * Black Matte VTC - Partner Payments Page
 * Pure view layer - ALL data from API
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { partnerService } from '../../services/partnerService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  CreditCard,
  AlertTriangle,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  TrendingUp,
  FileText,
  CheckCircle
} from 'lucide-react';

// Ledger entry type badge
const EntryTypeBadge = ({ type }) => {
  const typeConfig = {
    booking_charge: { label: 'Booking', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: ArrowUpRight },
    payment: { label: 'Payment', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: ArrowDownRight },
    invoice_issued: { label: 'Invoice', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: FileText },
    adjustment: { label: 'Adjustment', className: 'bg-purple-500/10 text-purple-400 border-purple-500/20', icon: TrendingUp },
  };
  
  const config = typeConfig[type] || typeConfig.adjustment;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`flex items-center gap-1 ${config.className}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

const PartnerPayments = () => {
  const [balanceData, setBalanceData] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const [balance, ledger] = await Promise.all([
        partnerService.getBalance(),
        partnerService.getLedger(50)
      ]);
      setBalanceData(balance);
      setLedgerEntries(ledger.entries || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={fetchData} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Check blocking from API
  const hasOverdue = balanceData?.has_overdue_invoices;
  const isOverLimit = !balanceData?.booking_allowed && !hasOverdue;

  return (
    <div className="space-y-6" data-testid="partner-payments">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Payments & Balance</h1>
          <p className="text-neutral-500">
            View your account balance and transaction history
          </p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={loading}
          className="border-neutral-700 hover:bg-neutral-800"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Warning Banners */}
      {hasOverdue && (
        <div 
          className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3"
          data-testid="overdue-banner"
        >
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-400">Overdue Invoices</p>
            <p className="text-xs text-red-400/80 mt-1">
              You have overdue invoices. Please settle them to continue booking.
            </p>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            asChild
          >
            <Link to="/partner/invoices?status=overdue">View Overdue</Link>
          </Button>
        </div>
      )}

      {isOverLimit && (
        <div 
          className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-start gap-3"
          data-testid="credit-limit-banner"
        >
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-400">Credit Limit Reached</p>
            <p className="text-xs text-amber-400/80 mt-1">
              Your credit limit has been reached. Make a payment to continue booking.
            </p>
          </div>
        </div>
      )}

      {/* Balance Cards - ALL from API */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {loading ? (
          <>
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-neutral-900 border-neutral-800">
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-24 bg-neutral-800 mb-3" />
                  <Skeleton className="h-8 w-32 bg-neutral-800" />
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <>
            {/* Current Balance */}
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-400 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Current Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white" data-testid="current-balance">
                  €{balanceData?.balance?.toFixed(2) || '0.00'}
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Amount owed
                </p>
              </CardContent>
            </Card>

            {/* Credit Available */}
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-400 flex items-center gap-2">
                  {balanceData?.booking_allowed ? (
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                  )}
                  Credit Available
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${balanceData?.booking_allowed ? 'text-emerald-400' : 'text-red-400'}`} data-testid="credit-available">
                  €{balanceData?.credit_available?.toFixed(2) || '0.00'}
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  of €{balanceData?.credit_limit?.toFixed(0)} limit
                </p>
              </CardContent>
            </Card>

            {/* Credit Limit */}
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-400 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Credit Limit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  €{balanceData?.credit_limit?.toFixed(0) || '0'}
                </div>
                <div className="mt-2">
                  {/* Usage bar - visual only */}
                  <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${
                        balanceData?.booking_allowed ? 'bg-emerald-500' : 'bg-red-500'
                      }`}
                      style={{ 
                        width: `${Math.min(100, ((balanceData?.balance || 0) / (balanceData?.credit_limit || 1)) * 100)}%` 
                      }}
                    />
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">
                    {Math.round(((balanceData?.balance || 0) / (balanceData?.credit_limit || 1)) * 100)}% used
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Payment Instructions */}
      <Card className="bg-neutral-900 border-neutral-800">
        <CardHeader>
          <CardTitle className="text-white">Make a Payment</CardTitle>
          <CardDescription>
            To make a payment, please use one of the following methods
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Bank Transfer */}
            <div className="p-4 bg-neutral-800/50 rounded-lg">
              <h4 className="text-sm font-medium text-white mb-3">Bank Transfer</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-500">IBAN</span>
                  <span className="text-neutral-300 font-mono">FR76 XXXX XXXX XXXX</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">BIC</span>
                  <span className="text-neutral-300 font-mono">BNPAFRPP</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Reference</span>
                  <span className="text-neutral-300">Your invoice number</span>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="p-4 bg-neutral-800/50 rounded-lg">
              <h4 className="text-sm font-medium text-white mb-3">Need Assistance?</h4>
              <p className="text-sm text-neutral-400 mb-3">
                Contact our billing team for payment arrangements or questions.
              </p>
              <Button variant="outline" size="sm" className="border-neutral-700">
                Contact Billing
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card className="bg-neutral-900 border-neutral-800">
        <CardHeader>
          <CardTitle className="text-white">Transaction History</CardTitle>
          <CardDescription>
            Recent account activity from your ledger
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full bg-neutral-800" />
              ))}
            </div>
          ) : ledgerEntries.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="text-neutral-400">Date</TableHead>
                  <TableHead className="text-neutral-400">Type</TableHead>
                  <TableHead className="text-neutral-400">Description</TableHead>
                  <TableHead className="text-neutral-400 text-right">Amount</TableHead>
                  <TableHead className="text-neutral-400 text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledgerEntries.map((entry) => (
                  <TableRow 
                    key={entry.entry_id} 
                    className="border-neutral-800 hover:bg-neutral-800/50"
                    data-testid={`ledger-entry-${entry.entry_id}`}
                  >
                    <TableCell className="text-neutral-300">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-neutral-500" />
                        {new Date(entry.created_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <EntryTypeBadge type={entry.entry_type} />
                    </TableCell>
                    <TableCell className="text-neutral-300 max-w-[200px] truncate">
                      {entry.description}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${
                      entry.entry_type === 'payment' ? 'text-emerald-400' : 'text-amber-400'
                    }`}>
                      {entry.entry_type === 'payment' ? '-' : '+'}€{Math.abs(entry.amount).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right text-white font-medium">
                      €{entry.balance_after?.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 text-neutral-700 mx-auto mb-3" />
              <p className="text-neutral-500">No transactions yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Count */}
      <div className="text-sm text-neutral-500">
        Showing {ledgerEntries.length} transaction{ledgerEntries.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default PartnerPayments;
